(() => {
  const SELECTORS = {overlay: '#CartRewardsOverlay', overlayReward: '#CartRewardsOverlayLive'};
  const nativeFetch = window.fetch.bind(window);
  const root = window.Shopify?.routes?.root || '/';
  const rewardProperty = '_44_tints_reward';
  const optOutAttribute = '_44_tints_reward_opt_out';
  const manualCountryAttribute = '_44_tints_reward_manual_country';
  const observedCountryAttribute = '_44_tints_reward_country';
  const rewardCode = 'FREESTICKERS100';
  const legacyRewardCode = 'FREESTICKERS50';
  let pending = null;
  let rerun = false;
  let timer;
  let checkingOut = false;
  let preference = null;
  let preferenceRevision = 0;
  let action = '';
  let actionRevision = 0;
  let rewardError = '';

  const component = () => document.querySelector('[data-cart-reward-progress]');
  const label = (name, fallback) => component()?.dataset[name] || fallback;
  const threshold = () => Number(component()?.dataset.stickerThreshold || 10000);
  const variantId = () => Number(component()?.dataset.stickerVariantId || 0);
  const isReward = item => item.properties?.[rewardProperty] === 'Free Sticker Pack';
  const subtotal = cart => cart.items.reduce((sum, item) => sum + (isReward(item) ? 0 : item.final_line_price), 0);
  const optedOut = cart => preference ? preference.optOut : ['true', '1'].includes(String(cart.attributes?.[optOutAttribute]));
  const country = () => component()?.dataset.country || 'UNKNOWN';
  const automaticRewardAllowed = () => {
    const data = component()?.dataset || {};
    const upper = data.knownRateUpper?.trim();
    const limit = data.autoAddMaxShippingCost?.trim();
    return country() === 'US' && data.knownShippingCurrency === 'USD' && Boolean(upper) && Boolean(limit)
      && Number.isFinite(Number(upper)) && Number.isFinite(Number(limit)) && Number(upper) >= 0 && Number(upper) <= Number(limit);
  };
  const rewardAllowed = cart => {
    const manualCountry = preference ? preference.manualCountry : cart.attributes?.[manualCountryAttribute];
    return !optedOut(cart) && (automaticRewardAllowed() || manualCountry === country());
  };
  const readJSON = async (path, body) => {
    const response = await nativeFetch(root + path, body === undefined
      ? {credentials: 'same-origin', cache: 'no-store'}
      : {method: 'POST', credentials: 'same-origin', headers: {'Content-Type': 'application/json'}, body: JSON.stringify(body)});
    const data = await response.json();
    if (!response.ok || data.status >= 400 || data.errors) throw new Error(data.description || 'Unable to update cart rewards');
    return data;
  };
  const parseSection = (html, selector) => html ? new DOMParser().parseFromString(html, 'text/html').querySelector(selector) : null;
  const showError = (message = label('error', 'We couldn’t update your cart. Please try again.')) => {
    document.querySelectorAll('[data-cart-reward-error]').forEach(element => {
      element.textContent = message;
      element.hidden = !message;
    });
  };
  const setBusy = () => {
    document.querySelectorAll('[data-cart-reward-progress]').forEach(element => element.setAttribute('aria-busy', action ? 'true' : 'false'));
    document.querySelectorAll('[data-cart-reward-add], [data-cart-reward-remove]').forEach(button => {
      if (!button.dataset.rewardOriginalLabel) button.dataset.rewardOriginalLabel = button.textContent;
      button.disabled = Boolean(action);
      button.textContent = action === 'adding'
        ? label('addingLabel', 'Adding…')
        : action === 'removing' ? label('removingLabel', 'Removing…') : button.dataset.rewardOriginalLabel;
    });
  };
  const refreshCartSurfaces = async cart => {
    const surfaces = [
      ['cart-icon-bubble', '#cart-icon-bubble', '#shopify-section-cart-icon-bubble'],
      ['cart-drawer', '#CartDrawer', '#CartDrawer'],
      ['main-cart-items', '#main-cart-items .js-contents', '#main-cart-items .js-contents'],
      ['main-cart-footer', '#main-cart-footer .js-contents', '#main-cart-footer .js-contents'],
      ['cart-notification-button', '#cart-notification-button', '#shopify-section-cart-notification-button']
    ].filter(([, selector]) => document.querySelector(selector));
    const names = [...surfaces.map(([name]) => name), 'cart-reward-progress'];
    const sections = {};
    // Shopify accepts at most five sections in a rendering request.
    for (let index = 0; index < names.length; index += 5) {
      Object.assign(sections, await readJSON('cart?sections=' + names.slice(index, index + 5).join(',')));
    }
    const focusedId = document.activeElement?.id;
    const drawer = document.querySelector('cart-drawer');
    const drawerOpen = drawer?.classList.contains('active');
    for (const [name, selector, sourceSelector] of surfaces) {
      const current = document.querySelector(selector);
      const fresh = parseSection(sections[name], sourceSelector);
      // These sections render inside existing links/containers; preserve their listeners.
      if (current && fresh) current.innerHTML = fresh.innerHTML;
    }
    const fresh = parseSection(sections['cart-reward-progress'], '[data-cart-reward-progress]');
    if (fresh) document.querySelectorAll('[data-cart-reward-progress]').forEach(current => current.replaceWith(fresh.cloneNode(true)));
    if (cart) {
      drawer?.classList.toggle('is-empty', cart.item_count === 0);
      document.querySelector('cart-items')?.classList.toggle('is-empty', cart.item_count === 0);
      document.getElementById('main-cart-footer')?.classList.toggle('is-empty', cart.item_count === 0);
    }
    setBusy();
    showError(rewardError);
    const focused = focusedId ? document.getElementById(focusedId) : null;
    if (drawerOpen && typeof trapFocus === 'function') {
      const inner = drawer.querySelector('.drawer__inner');
      const focusTarget = focused && !focused.disabled && drawer.contains(focused) ? focused : drawer.querySelector('[data-cart-reward-add]:not(:disabled), [data-cart-reward-remove]:not(:disabled)') || drawer.querySelector('.drawer__close');
      if (inner && focusTarget) trapFocus(inner, focusTarget);
    } else if (focused && document.activeElement === document.body) {
      focused.focus({preventScroll: true});
    }
  };
  const choose = optOut => {
    preference = {optOut, manualCountry: optOut ? '' : country(), revision: ++preferenceRevision};
    // This synchronous guard takes effect even if an earlier add request is in flight.
    rerun = true;
  };
  const persistPreference = async cart => {
    const intent = preference;
    const currentCountry = country();
    const attributes = {};
    if (cart.attributes?.[observedCountryAttribute] !== currentCountry) {
      attributes[observedCountryAttribute] = currentCountry;
      attributes[manualCountryAttribute] = '';
    }
    if (intent) {
      attributes[optOutAttribute] = String(intent.optOut);
      attributes[manualCountryAttribute] = intent.manualCountry === currentCountry ? intent.manualCountry : '';
    }
    if (Object.entries(attributes).some(([key, value]) => cart.attributes?.[key] !== value)) cart = await readJSON('cart/update.js', {attributes});
    return cart;
  };
  const removeRewards = async cart => {
    // Fetch each new line key from the response: discounts can change Shopify line keys.
    for (let count = 0; cart.items.some(isReward) && count < 20; count++) {
      const item = cart.items.find(isReward);
      cart = await readJSON('cart/change.js', {id: item.key, quantity: 0});
    }
    if (cart.items.some(isReward)) throw new Error('Unable to remove sticker reward');
    return cart;
  };
  const declineUnavailableReward = async cart => {
    choose(true);
    cart = await persistPreference(cart);
    cart = await removeRewards(cart);
    rewardError = label('addError', 'We couldn’t add your free stickers. You can retry or continue without them.');
    return cart;
  };
  const reconcile = async () => {
    let cart = await persistPreference(await readJSON('cart.js'));
    let rewards = cart.items.filter(isReward);
    if (subtotal(cart) < threshold() || !rewardAllowed(cart) || rewards.length > 1 || rewards.some(item => item.quantity !== 1)) {
      cart = await removeRewards(cart);
    }

    if (subtotal(cart) >= threshold() && rewardAllowed(cart) && !cart.items.some(isReward)) {
      const id = variantId();
      if (!id) cart = await declineUnavailableReward(cart);
      else {
        try {
          await readJSON('cart/add.js', {items: [{id, quantity: 1, properties: {[rewardProperty]: 'Free Sticker Pack'}}]});
          cart = await readJSON('cart.js');
        } catch (error) {
          // An optional reward must not create repeated paid additions or block a valid order.
          cart = await declineUnavailableReward(await readJSON('cart.js'));
        }
      }
    }

    cart = await persistPreference(cart);
    if (!rewardAllowed(cart) || subtotal(cart) < threshold()) cart = await removeRewards(cart);
    const hasLegacyRewardCode = (cart.discount_codes || []).some(item => String(item.code).toUpperCase() === legacyRewardCode);
    if (subtotal(cart) >= threshold() && rewardAllowed(cart) && cart.items.some(isReward)
      && (hasLegacyRewardCode || cart.items.some(item => isReward(item) && item.final_line_price > 0))) {
      const codes = new Set((cart.discount_codes || []).map(item => item.code).filter(code => code && String(code).toUpperCase() !== legacyRewardCode));
      if (![...codes].some(code => String(code).toUpperCase() === rewardCode)) codes.add(rewardCode);
      try { if (sessionStorage.getItem('tints-tint-bogo-claimed') === '1') codes.add('TINTBOGO'); } catch (_) {}
      try { cart = await readJSON('cart/update.js', {discount: [...codes].join(',')}); }
      catch (error) { cart = await declineUnavailableReward(await readJSON('cart.js')); }
    }

    // Removal can be requested during discount verification. Persist it before cleanup.
    cart = await persistPreference(cart);
    if (!rewardAllowed(cart) || subtotal(cart) < threshold()) cart = await removeRewards(cart);
    rewards = cart.items.filter(isReward);
    if (subtotal(cart) >= threshold() && rewardAllowed(cart) && (rewards.length !== 1 || rewards[0].quantity !== 1 || rewards[0].final_line_price !== 0)) {
      cart = await declineUnavailableReward(cart);
    }

    const intent = preference;
    const attributes = {
      _44_tints_reward_version: '2026-10-06-optional-country',
      _44_tints_reward_status: subtotal(cart) < threshold() ? 'below_threshold' : optedOut(cart) ? 'free_pack_declined' : !rewardAllowed(cart) ? 'shipping_confirmation_required' : 'free_pack_verified',
      _44_tints_reward_subtotal_cents: String(subtotal(cart))
    };
    if (intent) attributes[optOutAttribute] = String(intent.optOut);
    if (Object.entries(attributes).some(([key, value]) => cart.attributes?.[key] !== value)) cart = await readJSON('cart/update.js', {attributes});
    await refreshCartSurfaces(cart);
    if (intent && preference?.revision === intent.revision) preference = null;
    return cart;
  };
  const sync = () => {
    if (pending) { rerun = true; return pending; }
    pending = (async () => {
      let cart;
      do { rerun = false; cart = await reconcile(); } while (rerun);
      return cart;
    })().catch(error => { showError(); throw error; }).finally(() => { pending = null; });
    return pending;
  };
  const schedule = () => {
    window.clearTimeout(timer);
    timer = window.setTimeout(() => sync().catch(error => console.error('[44 Tints cart rewards]', error)), 80);
  };
  const requestPreference = (optOut, trigger) => {
    const surface = trigger?.closest?.('cart-notification, cart-drawer, #CartRewardsOverlay, #main-cart-footer')
      || (trigger?.closest?.('cart-items') ? document.getElementById('main-cart-footer') : null);
    choose(optOut);
    rewardError = '';
    action = optOut ? 'removing' : 'adding';
    const revision = ++actionRevision;
    setBusy();
    showError('');
    sync().catch(error => console.error('[44 Tints cart rewards]', error)).finally(() => {
      if (revision === actionRevision) {
        action = '';
        setBusy();
        // The clicked control is replaced with the opposite action after rendering.
        // Restore focus within the same cart surface without needing duplicate IDs.
        if (surface?.isConnected && (document.activeElement === document.body || surface.contains(document.activeElement))) {
          const button = surface.querySelector(optOut ? '[data-cart-reward-add]' : '[data-cart-reward-remove]');
          const focusTarget = button || surface.querySelector('.cart-rewards__headline');
          if (focusTarget) {
            if (!button) focusTarget.setAttribute('tabindex', '-1');
            focusTarget.focus({preventScroll: true});
          }
        }
      }
    });
  };
  const interceptRemoval = event => {
    const target = event.target;
    if (!target?.closest?.('[data-sticker-reward-key]')) return false;
    const row = target.closest('[data-sticker-reward-key]');
    const input = row.querySelector('input[name="updates[]"]');
    const remove = event.type === 'click' && target.closest('cart-remove-button');
    const minus = event.type === 'click' && target.closest('button[name="minus"]') && input && Number(input.value) <= 1;
    const zero = ['input', 'change'].includes(event.type) && target.matches('input[name="updates[]"]') && target.value.trim() !== '' && Number(target.value) <= 0;
    if (!remove && !minus && !zero) return false;
    event.preventDefault();
    event.stopImmediatePropagation();
    requestPreference(true, target);
    return true;
  };
  document.addEventListener('input', interceptRemoval, true);
  document.addEventListener('change', interceptRemoval, true);

  const isCartMutation = input => {
    try {
      const url = new URL(typeof input === 'string' ? input : input.url, location.href);
      return url.origin === location.origin && /\/cart\/(add|change|update|clear)(\.js)?\/?$/.test(url.pathname);
    } catch (_) { return false; }
  };
  window.fetch = async (...args) => {
    const response = await nativeFetch(...args);
    if (response.ok && isCartMutation(args[0])) schedule();
    return response;
  };
  const originalOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function(method, url, ...args) {
    if (isCartMutation(String(url))) this.addEventListener('load', () => { if (this.status >= 200 && this.status < 300) schedule(); }, {once: true});
    return originalOpen.call(this, method, url, ...args);
  };
  const checkout = async event => {
    event.preventDefault();
    event.stopImmediatePropagation();
    if (checkingOut) return;
    checkingOut = true;
    try { await sync(); window.location.assign(root + 'checkout'); }
    catch (error) {
      showError();
      window.alert(label('error', 'We couldn’t update your cart. Please try again.'));
    }
    finally { checkingOut = false; }
  };
  document.addEventListener('click', event => {
    if (interceptRemoval(event)) return;
    const rewardAction = event.target.closest('[data-cart-reward-add], [data-cart-reward-remove]');
    if (rewardAction) {
      event.preventDefault();
      event.stopImmediatePropagation();
      if (!action) requestPreference(rewardAction.hasAttribute('data-cart-reward-remove'), rewardAction);
      return;
    }
    // Cart refreshes replace the backdrop; delegation keeps its close behavior intact.
    if (event.target.closest('#CartDrawer-Overlay')) {
      const drawer = document.querySelector('cart-drawer');
      if (drawer?.close) { event.preventDefault(); event.stopImmediatePropagation(); drawer.close(); }
      return;
    }
    const target = event.target.closest('button[name="checkout"],input[name="checkout"],a[href]');
    if (!target) return;
    if (target.name === 'checkout') return checkout(event);
    try { const url = new URL(target.href, location.href); if (url.origin === location.origin && /\/checkout\/?$/.test(url.pathname)) checkout(event); } catch (_) {}
  }, true);
  document.addEventListener('submit', event => {
    if (event.submitter?.name === 'checkout') checkout(event);
  }, true);
  document.addEventListener('cart:refresh', schedule);
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') subscribe(PUB_SUB_EVENTS.cartUpdate, schedule);
  window.addEventListener('pageshow', schedule);
  window.addEventListener('focus', schedule);
  if (!customElements.get('cart-reward-progress')) customElements.define('cart-reward-progress', class extends HTMLElement {});
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', schedule, {once: true});
  else schedule();

  const setupRewardsOverlay = () => {
    const overlay = document.querySelector(SELECTORS.overlay);
    if (!overlay) return;
    const openers = document.querySelectorAll('[data-cart-rewards-open]');
    const open = () => {
      schedule();
      openers.forEach(button => button.classList.remove('is-reward-hovered'));
      document.documentElement.classList.add('cart-rewards-overlay-open');
      if (typeof overlay.showModal === 'function') overlay.showModal();
      else overlay.setAttribute('open', '');
    };
    const close = () => {
      if (typeof overlay.close === 'function') overlay.close();
      else overlay.removeAttribute('open');
    };
    openers.forEach(button => {
      button.addEventListener('pointerenter', () => button.classList.add('is-reward-hovered'));
      button.addEventListener('pointerleave', () => button.classList.remove('is-reward-hovered'));
      button.addEventListener('focus', () => button.classList.add('is-reward-hovered'));
      button.addEventListener('blur', () => button.classList.remove('is-reward-hovered'));
      button.addEventListener('click', open);
    });
    overlay.querySelectorAll('[data-cart-rewards-close]').forEach(button => button.addEventListener('click', close));
    overlay.addEventListener('click', event => { if (event.target === overlay) close(); });
    overlay.addEventListener('close', () => document.documentElement.classList.remove('cart-rewards-overlay-open'));
    overlay.addEventListener('cancel', () => document.documentElement.classList.remove('cart-rewards-overlay-open'));
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', setupRewardsOverlay, {once: true});
  else setupRewardsOverlay();
})();
